import { useState } from "react";
import { Check, X } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { RISK_META, RISK_FALLBACK } from "@/lib/risk";

/**
 * Clickable Risk badge that opens a dialog with Activity and Dependency breakdowns.
 * The dialog has tabs to switch between Activity and Dependency views.
 */
export default function ClassificationBreakdown({ firm }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("activity"); // "activity" or "dependency"

  // Calculate tenure
  const activationDate = new Date(firm.activation_date);
  const today = new Date();
  const tenureDays = Math.floor((today - activationDate) / (1000 * 60 * 60 * 24));

  // Determine tenure bucket
  let tenureBucket, tenureLabel, measurementPeriod, periodKey;
  if (tenureDays <= 30) {
    tenureBucket = "0-30d";
    tenureLabel = "0-30 days old";
    measurementPeriod = "last 30 days";
    periodKey = "30";
  } else if (tenureDays <= 90) {
    tenureBucket = "31-90d";
    tenureLabel = "31-90 days old";
    measurementPeriod = "last 90 days";
    periodKey = "90";
  } else {
    tenureBucket = "91d+";
    tenureLabel = `${tenureDays} days old`;
    measurementPeriod = "last 90 days";
    periodKey = "90";
  }

  // Parse num_users and cap at 10
  let numUsers =
    !firm.num_users || firm.num_users === "unlimited" || firm.num_users === "-"
      ? 1
      : parseInt(firm.num_users) || 1;
  
  // Cap at 10 for all per-user calculations
  numUsers = Math.min(10, Math.max(1, numUsers));
  
  const usersNote = numUsers === 10 && parseInt(firm.num_users) > 10 
    ? ` (users: capped at 10)` 
    : (numUsers === 1 && firm.num_users !== "1" ? " (users: defaulted to 1)" : "");

  // Get event counts
  const getEC = (period, event) => {
    return parseInt(firm.event_counts?.[period]?.[event] || 0);
  };

  // Activity metrics
  const importBooks = getEC(periodKey, "Import Books") / numUsers;
  const statements = getEC(periodKey, "Statement Generation") / numUsers;
  const scrutiny = getEC(periodKey, "Ledger Scrutiny") / numUsers;
  const issues = getEC(periodKey, "Issues Raised") / numUsers;
  const clients = (firm.total_clients || 0) / numUsers;

  // Dependency metrics
  const grafana = firm.grafana_cumulative || {};
  const depClients = (grafana.total_clients || 0) / numUsers;
  const depCompleted = (grafana.total_closed_assignments || 0) / numUsers;
  const depOpen = (grafana.total_open_assignments || 0) / numUsers;
  const depLedgers = grafana.total_ledgers || 0;
  const depVouchers = grafana.total_vouchers || 0;

  // Activity thresholds
  // Helper: Evaluate individual metric level
  const evaluateMetric = (value, high, mediumMin, mediumMax = null) => {
    if (value >= high) return "High";
    if (mediumMax !== null) {
      if (value >= mediumMin && value < mediumMax) return "Medium";
    } else {
      if (value >= mediumMin) return "Medium";
    }
    return "Low";
  };

  let activityRows;
  let metricLevels = [];
  
  if (tenureDays <= 30) {
    const ibLevel = evaluateMetric(importBooks, 5, 1, 5);
    const stmtLevel = evaluateMetric(statements, 17, 3, 17);
    
    activityRows = [
      { metric: "Import Books/user", high: "≥ 5", medium: "1-4.9", actual: importBooks, level: ibLevel },
      { metric: "Statements/user", high: "≥ 17", medium: "3-16.9", actual: statements, level: stmtLevel },
    ];
    
    metricLevels = [ibLevel, stmtLevel];
  } else if (tenureDays <= 90) {
    const clientsLevel = evaluateMetric(clients, 8, 0.01);
    const ibLevel = evaluateMetric(importBooks, 10, 2, 10);
    const stmtLevel = evaluateMetric(statements, 31, 5, 31);
    const scrutinyLevel = evaluateMetric(scrutiny, 1, 0.01, 1);
    const issuesLevel = evaluateMetric(issues, 1, 0.01, 1);
    
    activityRows = [
      { metric: "Clients/user", high: "≥ 8", medium: "> 0", actual: clients, level: clientsLevel },
      { metric: "Import Books/user", high: "≥ 10", medium: "2-9.9", actual: importBooks, level: ibLevel },
      { metric: "Statements/user", high: "≥ 31", medium: "5-30.9", actual: statements, level: stmtLevel },
      { metric: "Ledger Scrutiny/user", high: "≥ 1", medium: "< 1", actual: scrutiny, level: scrutinyLevel },
      { metric: "Issues Raised/user", high: "≥ 1", medium: "< 1", actual: issues, level: issuesLevel },
    ];
    
    metricLevels = [clientsLevel, ibLevel, stmtLevel, scrutinyLevel, issuesLevel];
  } else {
    const ibLevel = evaluateMetric(importBooks, 16, 3, 16);
    const stmtLevel = evaluateMetric(statements, 52, 9, 52);
    const scrutinyLevel = evaluateMetric(scrutiny, 4, 1, 4);
    const issuesLevel = evaluateMetric(issues, 10, 0);
    
    activityRows = [
      { metric: "Import Books/user", high: "≥ 16", medium: "3-15.9", actual: importBooks, level: ibLevel },
      { metric: "Statements/user", high: "≥ 52", medium: "9-51.9", actual: statements, level: stmtLevel },
      { metric: "Ledger Scrutiny/user", high: "≥ 4", medium: "1-3.9", actual: scrutiny, level: scrutinyLevel },
      { metric: "Issues Raised/user", high: "≥ 10", medium: "≥ 0", actual: issues, level: issuesLevel },
    ];
    
    metricLevels = [ibLevel, stmtLevel, scrutinyLevel, issuesLevel];
  }

  // Overall Activity Level = worst (minimum) level across all metrics
  let activityLevel = "High";
  if (metricLevels.includes("Low")) {
    activityLevel = "Low";
  } else if (metricLevels.includes("Medium")) {
    activityLevel = "Medium";
  }

  // Build verdict
  let activityVerdict = "";
  if (activityLevel === "High") {
    activityVerdict = "All metrics meet High thresholds → High Activity";
  } else {
    const draggingMetrics = activityRows.filter(r => r.level === activityLevel);
    const metricNames = draggingMetrics.map(r => `${r.metric} (${r.level})`).join(", ");
    activityVerdict = `${activityLevel} Activity — dragged down by: ${metricNames}. Overall level = worst individual metric level.`;
  }
  // Dependency thresholds with individual level evaluation
  const depClientsLevel = evaluateMetric(depClients, 10, 6);
  const depCompletedLevel = evaluateMetric(depCompleted, 10, 6);
  const depOpenLevel = evaluateMetric(depOpen, 10, 6);
  const depLedgersLevel = depLedgers >= 4000 ? "High" : "Low"; // No Medium for ledgers
  const depVouchersLevel = depVouchers >= 40000 ? "High" : "Low"; // No Medium for vouchers
  
  const dependencyRows = [
    { metric: "Clients/user", high: "≥ 10", medium: "≥ 6", actual: depClients, level: depClientsLevel },
    { metric: "Completed Assignments/user", high: "≥ 10", medium: "≥ 6", actual: depCompleted, level: depCompletedLevel },
    { metric: "Open Assignments/user", high: "≥ 10", medium: "≥ 6", actual: depOpen, level: depOpenLevel },
    { metric: "Total Ledgers", high: "≥ 4,000", medium: "—", actual: depLedgers, level: depLedgersLevel },
    { metric: "Total Vouchers", high: "≥ 40,000", medium: "—", actual: depVouchers, level: depVouchersLevel },
  ];

  const depMetricLevels = [depClientsLevel, depCompletedLevel, depOpenLevel, depLedgersLevel, depVouchersLevel];

  // Overall Dependency Level = worst (minimum) level across all metrics
  let dependencyLevel = "High";
  if (depMetricLevels.includes("Low")) {
    dependencyLevel = "Low";
  } else if (depMetricLevels.includes("Medium")) {
    dependencyLevel = "Medium";
  }

  // Build dependency verdict
  let dependencyVerdict = "";
  if (dependencyLevel === "High") {
    dependencyVerdict = "All metrics meet High thresholds → High Dependency";
  } else {
    const draggingDepMetrics = dependencyRows.filter(r => r.level === dependencyLevel);
    const depMetricNames = draggingDepMetrics.map(r => `${r.metric} (${r.level})`).join(", ");
    dependencyVerdict = `${dependencyLevel} Dependency — dragged down by: ${depMetricNames}. Overall level = worst individual metric level.`;
  }


  const levelMeta = {
    High: { color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200" },
    Medium: { color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200" },
    Low: { color: "text-red-700", bg: "bg-red-50", border: "border-red-200" },
  };

  const activityMeta = levelMeta[activityLevel];
  const dependencyMeta = levelMeta[dependencyLevel];
  const riskMeta = RISK_META[firm.risk] || RISK_FALLBACK;

  return (
    <>
      {/* Clickable Risk Badge */}
      <button
        onClick={() => setIsOpen(true)}
        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium border whitespace-nowrap ${riskMeta.badge} hover:shadow-sm transition-all cursor-pointer`}
        data-testid={`risk-badge-clickable-${riskMeta.key}`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${riskMeta.dot}`} />
        {riskMeta.label}
      </button>

      {/* Classification Breakdown Dialog with Tabs */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              Risk Classification Breakdown
              <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium border ${riskMeta.badge}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${riskMeta.dot}`} />
                {riskMeta.label}
              </span>
            </DialogTitle>
          </DialogHeader>

          {/* Tab Switcher */}
          <div className="flex gap-2 border-b border-gray-200 mt-4">
            <button
              onClick={() => setActiveTab("activity")}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "activity"
                  ? "border-blue-500 text-blue-700"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Activity Level
              <span className={`ml-2 inline-flex items-center gap-1 ${activityMeta.bg} rounded-full px-2 py-0.5 text-[10px] font-semibold ${activityMeta.color}`}>
                {activityLevel}
              </span>
            </button>
            <button
              onClick={() => setActiveTab("dependency")}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "dependency"
                  ? "border-blue-500 text-blue-700"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Dependency Level
              <span className={`ml-2 inline-flex items-center gap-1 ${dependencyMeta.bg} rounded-full px-2 py-0.5 text-[10px] font-semibold ${dependencyMeta.color}`}>
                {dependencyLevel}
              </span>
            </button>
          </div>

          {/* Activity Tab Content */}
          {activeTab === "activity" && (
            <div className="space-y-4 mt-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-gray-700">
              <strong>{tenureLabel}</strong> → using {tenureBucket} thresholds, measured over {measurementPeriod}
            </div>

            <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-2.5 text-left font-semibold text-gray-900">Metric</th>
                  <th className="px-4 py-2.5 text-left font-semibold text-gray-900">Required for High</th>
                  <th className="px-4 py-2.5 text-left font-semibold text-gray-900">Required for Medium</th>
                  <th className="px-4 py-2.5 text-right font-semibold text-gray-900">Actual Value</th>
                </tr>
              </thead>
              <tbody>
                {activityRows.map((row, idx) => {
                  const levelMeta = {
                    High: { color: "text-emerald-700", bg: "bg-emerald-50", icon: <Check size={14} weight="bold" className="text-emerald-600" /> },
                    Medium: { color: "text-amber-700", bg: "bg-amber-50", icon: <Check size={14} weight="bold" className="text-amber-600" /> },
                    Low: { color: "text-red-700", bg: "bg-red-50", icon: <X size={14} weight="bold" className="text-red-600" /> }
                  };
                  const meta = levelMeta[row.level];
                  
                  return (
                    <tr key={idx} className="border-b border-gray-100 last:border-b-0">
                      <td className="px-4 py-2.5 text-gray-700">{row.metric}</td>
                      <td className="px-4 py-2.5 text-gray-600 font-mono text-xs">{row.high}</td>
                      <td className="px-4 py-2.5 text-gray-600 font-mono text-xs">{row.medium}</td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <span className="font-mono text-xs text-gray-900">{row.actual.toFixed(2)}{usersNote}</span>
                          <span className={`inline-flex items-center gap-1 ${meta.bg} rounded-full px-2 py-0.5 text-[10px] font-semibold ${meta.color}`}>
                            {meta.icon}
                            {row.level}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-700 italic">
                {activityVerdict}
              </div>
            </div>
          )}

          {/* Dependency Tab Content */}
          {activeTab === "dependency" && (
            <div className="space-y-4 mt-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-gray-700">
              Dependency uses <strong>all-time accumulated values</strong> from the database
            </div>

            <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-2.5 text-left font-semibold text-gray-900">Metric</th>
                  <th className="px-4 py-2.5 text-left font-semibold text-gray-900">Required for High</th>
                  <th className="px-4 py-2.5 text-left font-semibold text-gray-900">Required for Medium</th>
                  <th className="px-4 py-2.5 text-right font-semibold text-gray-900">Actual Value</th>
                </tr>
              </thead>
              <tbody>
                {dependencyRows.map((row, idx) => {
                  const levelMeta = {
                    High: { color: "text-emerald-700", bg: "bg-emerald-50", icon: <Check size={14} weight="bold" className="text-emerald-600" /> },
                    Medium: { color: "text-amber-700", bg: "bg-amber-50", icon: <Check size={14} weight="bold" className="text-amber-600" /> },
                    Low: { color: "text-red-700", bg: "bg-red-50", icon: <X size={14} weight="bold" className="text-red-600" /> }
                  };
                  const meta = levelMeta[row.level];
                  const showUsers = row.metric.includes("/user");
                  
                  return (
                    <tr key={idx} className="border-b border-gray-100 last:border-b-0">
                      <td className="px-4 py-2.5 text-gray-700">{row.metric}</td>
                      <td className="px-4 py-2.5 text-gray-600 font-mono text-xs">{row.high}</td>
                      <td className="px-4 py-2.5 text-gray-600 font-mono text-xs">{row.medium}</td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <span className="font-mono text-xs text-gray-900">
                            {row.actual >= 1000 ? row.actual.toLocaleString() : row.actual.toFixed(2)}
                            {showUsers && usersNote}
                          </span>
                          <span className={`inline-flex items-center gap-1 ${meta.bg} rounded-full px-2 py-0.5 text-[10px] font-semibold ${meta.color}`}>
                            {meta.icon}
                            {row.level}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-700 italic">
                {dependencyVerdict}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
